import React from 'react';

import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { FormApi } from 'final-form';
import { FormSpy } from 'react-final-form';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { FieldArrayTemplate } from 'src/components/common';
import { CheckboxField, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useCurrentEmployerStaff, useEmployerStaff } from 'src/hooks/employerStaff';
import { theme } from 'src/theme';

import { INITIAL_VALUES, USER_INITIAL_VALUES } from './constants';
import {
  EmployerStaffInviteFields,
  EmployerStaffInviteUserFields,
  EmployerStaffInviteValues,
} from './types';
import { employerStaffInviteValidationSchema, prepareInviteData } from './utils';

export type EmployerStaffInviteModalFormProps = {
  onCloseModal: () => void;
  switchClosability: (status: boolean) => void;
  employerId?: string;
};

export const EmployerStaffInviteModalForm: React.FC<EmployerStaffInviteModalFormProps> = ({
  onCloseModal,
  switchClosability,
  employerId: employerIdProp,
}) => {
  const { createApolloContext } = useNotification();
  const { employerId: currentEmployerId } = useCurrentEmployerStaff();
  const { inviteEmployerStaff, inviteEmployerStaffByEmployerAdmin } = useEmployerStaff();

  const employerId = React.useMemo(
    () => employerIdProp || currentEmployerId,
    [currentEmployerId, employerIdProp],
  );

  const apolloContext = createApolloContext({
    successMessage: 'Employer Staff has been successfully invited',
  });

  const handleSubmit = React.useCallback(
    async (formValues: EmployerStaffInviteValues) => {
      if (!employerId) return;

      try {
        if (formValues.usersUploadedFile || !formValues.users?.length) {
          throw new Error('Not implemented yet');
        }

        const { employerStaffList } = prepareInviteData(formValues);

        if (currentEmployerId) {
          await inviteEmployerStaffByEmployerAdmin({
            variables: { employerStaffList },
            context: apolloContext,
          });
        } else {
          await inviteEmployerStaff({
            variables: { employerStaffList, employerId },
            context: apolloContext,
          });
        }

        switchClosability(true);
        onCloseModal();
      } catch {
        switchClosability(true);
      }
    },
    [
      apolloContext,
      currentEmployerId,
      employerId,
      inviteEmployerStaff,
      inviteEmployerStaffByEmployerAdmin,
      onCloseModal,
      switchClosability,
    ],
  );

  const handleUserIsAdminSelect = React.useCallback(
    (
      form: FormApi<EmployerStaffInviteValues, Partial<EmployerStaffInviteValues>>,
      index: number,
    ) => {
      const { values } = form.getState();

      const user = values.users?.[index];
      if (!user) return;
    },
    [],
  );

  return (
    <Form<EmployerStaffInviteValues>
      onSubmit={handleSubmit}
      initialValues={INITIAL_VALUES}
      subscription={{ submitting: true, dirty: true, valid: true }}
      validate={validateWithYupSchema(employerStaffInviteValidationSchema)}
    >
      {({ form, submitting, dirty, valid }) => {
        return (
          <React.Fragment>
            <FieldArrayTemplate
              label="Invitation"
              name={EmployerStaffInviteFields.users}
              onAdd={fields => fields.push(USER_INITIAL_VALUES)}
              onRemove={(fields, index) => fields.remove(index)}
              addIcon={<AddIcon />}
              containerProps={{ spacing: 2 }}
            >
              {({ renderFieldName, index }) => (
                <Box display="flex" flexDirection="column" gap={1}>
                  <Field
                    name={renderFieldName(EmployerStaffInviteUserFields.email)}
                    label="Email Address"
                    required
                    component={TextField}
                    fullWidth
                    disabled={submitting}
                  />
                  <Field
                    name={renderFieldName(EmployerStaffInviteUserFields.isAdmin)}
                    label="This user is Admin"
                    component={CheckboxField}
                    fullWidth
                    style={{ color: theme.palette.primary.main }}
                    disabled={submitting}
                  />
                  <FormSpy
                    subscription={{ values: true }}
                    onChange={() => handleUserIsAdminSelect(form, index)}
                  />
                </Box>
              )}
            </FieldArrayTemplate>

            <Box display="flex" justifyContent="center" gap={2} mt={2}>
              <Button onClick={onCloseModal} variant="outlined" color="error">
                Close
              </Button>
              <Button
                loading={submitting}
                type="submit"
                disabled={!dirty || submitting || !valid}
                variant="contained"
              >
                Send Invite
              </Button>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};
